// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Pressable, StyleSheet, View } from 'react-native';
import { Text } from '@/components/Themed';
import { useI18n } from '@/i18n';
import appColors from '@/theme/colors';

type Props = {
  title: string;
  onBack: () => void;
};

/**
 * Header for the mobile detail view: back to the full-screen menu plus the
 * current section title (the EditorPanel suppresses its own title on mobile).
 */
export default function MobileDetailHeader({ title, onBack }: Props) {
  const { t } = useI18n();
  return (
    <View style={styles.bar}>
      <Pressable
        testID="tour-edit-detail-back"
        style={styles.backButton}
        onPress={onBack}
        accessibilityRole="button"
        accessibilityLabel={t('tourEdit.mobileBackToMenuA11y')}
      >
        <Text style={styles.backChevron}>‹</Text>
        <Text style={styles.backLabel}>{t('tourEdit.mobileMenu')}</Text>
      </Pressable>
      <Text style={styles.title} numberOfLines={1}>
        {title}
      </Text>
      {/* Mirrors the back button width so the title centers optically. */}
      <View style={styles.spacer} />
    </View>
  );
}

const styles = StyleSheet.create({
  bar: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 10,
    backgroundColor: appColors.background.panel,
    borderBottomWidth: 1,
    borderBottomColor: appColors.border.default,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    paddingHorizontal: 8,
    paddingVertical: 2,
    minWidth: 72,
  },
  backChevron: {
    fontSize: 22,
    lineHeight: 24,
    color: appColors.accent.primary,
  },
  backLabel: {
    fontSize: 15,
    fontWeight: '600',
    color: appColors.accent.primary,
  },
  title: {
    flex: 1,
    textAlign: 'center',
    fontSize: 15,
    fontWeight: '700',
    color: appColors.text.primary,
  },
  spacer: {
    minWidth: 72,
  },
});
